import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { reportAPI } from '../../utils/api';

const formatMoney = (value) => `PHP ${Number.parseFloat(value || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getOrderNumber = (order) => order.receipt_number || order.transaction_id || order.order_id;

const getOrderTime = (order) => {
  const raw = order.created_at || order.order_date;
  if (!raw) return '-';
  return new Date(raw).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });
};

export default function DailyOrdersReport() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const hasAutoActionRun = useRef(false);

  const selectedDate = searchParams.get('date') || new Date().toISOString().slice(0, 10);
  const restaurantId = searchParams.get('restaurant_id') || '';
  const shouldAutoPrint = searchParams.get('autoprint') === '1';
  const shouldAutoExportPdf = searchParams.get('export') === 'pdf';

  useEffect(() => {
    const fetchDailyReport = async () => {
      try {
        setLoading(true);
        setError('');

        const params = { date: selectedDate };
        if (restaurantId) {
          params.restaurant_id = restaurantId;
        }

        const response = await reportAPI.getDaily(params);
        if (response.data?.success) {
          setReport(response.data.data);
        } else {
          setError('Failed to load daily report.');
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load daily report.');
      } finally {
        setLoading(false);
      }
    };

    fetchDailyReport();
  }, [selectedDate, restaurantId]);

  const tableRows = useMemo(() => {
    const orders = report?.orders || [];
    return orders.map((order) => ({
      orderNumber: getOrderNumber(order),
      time: getOrderTime(order),
      items: (order.items || [])
        .map((item) => `${item.quantity} x ${item.menu_item?.name || item.menuItem?.name || 'Item'}`)
        .join('\n'),
      total: Number.parseFloat(order.total_amount || 0),
    }));
  }, [report]);

  const exportPdf = () => {
    if (!report) return;

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    doc.setFontSize(14);
    doc.text('Daily Sales Report', 40, 48);
    doc.setFontSize(10);
    doc.text(`Selected date: ${report.date}`, 40, 66);
    if (report.restaurant_id) {
      doc.text(`Restaurant ID: ${report.restaurant_id}`, 40, 82);
    }

    autoTable(doc, {
      startY: report.restaurant_id ? 96 : 84,
      head: [['Order Number', 'Time', 'Items', 'Total Amount']],
      body: tableRows.map((row) => [row.orderNumber, row.time, row.items || '-', formatMoney(row.total)]),
      styles: { fontSize: 9, cellPadding: 5, valign: 'top' },
      headStyles: { fillColor: [36, 36, 36] },
      columnStyles: {
        0: { cellWidth: 110 },
        1: { cellWidth: 70 },
        2: { cellWidth: 260 },
        3: { cellWidth: 90, halign: 'right' },
      },
    });

    let y = doc.lastAutoTable.finalY + 18;
    doc.setFontSize(11);
    doc.text(`Grand total sales: ${formatMoney(report.summary?.total_sales || 0)}`, 40, y);
    y += 16;
    doc.text(`Total number of orders: ${report.summary?.total_orders || 0}`, 40, y);

    const paymentBreakdown = report.payment_breakdown || [];
    if (paymentBreakdown.length > 0) {
      y += 22;
      doc.setFontSize(10);
      doc.text('Payment method breakdown:', 40, y);
      y += 14;
      paymentBreakdown.forEach((entry) => {
        doc.text(
          `${String(entry.payment_method || 'cash').toUpperCase()}: ${entry.count} orders, ${formatMoney(entry.total)}`,
          40,
          y
        );
        y += 13;
      });
    }

    doc.save(`daily-sales-report-${report.date}.pdf`);
  };

  useEffect(() => {
    if (!report || hasAutoActionRun.current) {
      return;
    }

    if (shouldAutoExportPdf) {
      hasAutoActionRun.current = true;
      exportPdf();
      return;
    }

    if (shouldAutoPrint) {
      hasAutoActionRun.current = true;
      setTimeout(() => {
        window.print();
      }, 250);
    }
  }, [report, shouldAutoExportPdf, shouldAutoPrint]);

  if (loading) {
    return <div style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>Loading report...</div>;
  }

  if (error) {
    return <div style={{ padding: 24, color: '#991b1b', fontFamily: 'Arial, sans-serif' }}>{error}</div>;
  }

  const orders = report?.orders || [];

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh', padding: '20px 12px', fontFamily: 'Arial, sans-serif' }}>
      <style>
        {`
          .report-sheet {
            max-width: 1000px;
            margin: 0 auto;
            background: #fff;
            color: #111;
            border: 1px solid #ddd;
            padding: 20px;
          }
          .report-actions {
            display: flex;
            gap: 8px;
            margin-bottom: 14px;
            justify-content: flex-end;
          }
          .report-actions button {
            border: 1px solid #333;
            background: #fff;
            padding: 8px 10px;
            cursor: pointer;
            font-size: 12px;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 14px;
            font-size: 13px;
          }
          .report-table th, .report-table td {
            border: 1px solid #000;
            padding: 8px;
            vertical-align: top;
            text-align: left;
          }
          .report-table th {
            background: #f2f2f2;
          }
          .report-summary {
            margin-top: 16px;
            font-size: 14px;
          }
          @media print {
            body {
              background: #fff;
            }
            .report-actions {
              display: none;
            }
            .report-sheet {
              border: none;
              margin: 0;
              max-width: none;
              padding: 0;
            }
          }
        `}
      </style>

      <div className="report-sheet">
        <div className="report-actions">
          <button type="button" onClick={() => window.print()}>Print</button>
          <button type="button" onClick={exportPdf}>Export PDF</button>
        </div>

        <h1 style={{ margin: '0 0 4px 0', fontSize: 24 }}>Daily Sales Report</h1>
        <p style={{ margin: '0 0 2px 0', fontSize: 14 }}><strong>Selected date:</strong> {report.date}</p>
        {report.restaurant_id ? (
          <p style={{ margin: '0 0 2px 0', fontSize: 14 }}><strong>Restaurant ID:</strong> {report.restaurant_id}</p>
        ) : null}

        <table className="report-table">
          <thead>
            <tr>
              <th style={{ width: '16%' }}>Order Number</th>
              <th style={{ width: '12%' }}>Time</th>
              <th>List of Items (qty x item)</th>
              <th style={{ width: '16%' }}>Total Amount</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center' }}>No orders for selected date.</td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.order_id}>
                  <td>{getOrderNumber(order)}</td>
                  <td>{getOrderTime(order)}</td>
                  <td>
                    {(order.items || []).length === 0
                      ? '-'
                      : (order.items || []).map((item) => (
                          <div key={item.order_item_id || `${order.order_id}-${item.menu_item_id}`}>
                            {item.quantity} x {item.menu_item?.name || item.menuItem?.name || 'Item'}
                          </div>
                        ))}
                  </td>
                  <td>{formatMoney(order.total_amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="report-summary">
          <p><strong>Grand total sales:</strong> {formatMoney(report.summary?.total_sales || 0)}</p>
          <p><strong>Total number of orders:</strong> {report.summary?.total_orders || 0}</p>
          {(report.payment_breakdown || []).length > 0 ? (
            <div>
              <strong>Payment method breakdown:</strong>
              {(report.payment_breakdown || []).map((entry) => (
                <p key={entry.payment_method} style={{ margin: '4px 0 0 0' }}>
                  {String(entry.payment_method || 'cash').toUpperCase()}: {entry.count} orders, {formatMoney(entry.total)}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
